import { CheckCircle2, History, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import ListPagination from "@/components/shared/ListPagination";
import { formatRelativeTime } from "@/utils/dateUtils";

import {
  BRANDING_SHELL,
  BRANDING_SHELL_STYLE,
  BRANDING_CARD_PADDING,
  BRANDING_TILE_RADIUS,
  BRANDING_LABEL,
} from "../branding.tokens";
import BrandingSectionHeader from "./BrandingSectionHeader";

/**
 * The app's one relative clock. What was here wrapped the value in
 * `toZonedTime(value, <the browser's own zone>)` before measuring it, which is
 * an identity on every machine that will run it, and read as if a correction
 * were being applied. `formatRelativeTime` handles the missing and unparseable
 * cases too, so the try/catch around it went with the conversion.
 */
const formatPublishedAt = (value) => formatRelativeTime(value);

const initialsOf = (name, email) => {
  const source = (name || email || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

// Each row's own content box starts with py-3 (12px) of top padding before
// the title text begins — the dot column has to match that same inset
// before centering against the title's line-height, or it starts 12px too
// high (measured from the row's top edge instead of the row box's actual
// content edge).
const ROW_PT_PX = 12; // py-3
const TITLE_LEADING_PX = 20; // explicit line-height set on the title below
const DOT_SIZE_PX = 10; // h-2.5 / w-2.5
const DOT_OFFSET_PX = ROW_PT_PX + (TITLE_LEADING_PX - DOT_SIZE_PX) / 2; // 17px

const HistoryRow = ({ item, isLast, isCurrent, restoredFromVersion, disabled, onRestore }) => {
  return (
    <div className="flex gap-3 pb-5 last:pb-0">
      {/* Dot + connector share one narrow column, sized to the dot itself.
          The line is drawn from the dot's own position down to the next
          row's dot, so it always lines up regardless of how tall the card
          beside it grows (wrapped badges, longer names, etc). */}
      <div className="relative flex w-2.5 shrink-0 flex-col items-center self-stretch">
        <span
          className={`z-10 h-2.5 w-2.5 shrink-0 rounded-full shadow-sm border-2 border-background ${
            isCurrent ? "bg-emerald-500" : "bg-button-secondary"
          }`}
          style={{ marginTop: `${DOT_OFFSET_PX}px` }}
        />
        {!isLast && (
          <div
            className="pointer-events-none absolute bottom-[-1.25rem] w-[2px] bg-border-primary"
            style={{ top: `${DOT_OFFSET_PX + DOT_SIZE_PX}px` }}
          />
        )}
      </div>

      <div
        className={`flex flex-1 flex-col gap-1.5 border ${BRANDING_TILE_RADIUS} bg-background-secondary/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between`}
        style={BRANDING_SHELL_STYLE}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <p
              className="text-[13px] font-semibold text-text-primary"
              style={{ lineHeight: `${TITLE_LEADING_PX}px` }}
            >
              v{item?.version} — {item?.displayName || "Untitled"}
            </p>
            {isCurrent && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
                <CheckCircle2 className="h-3 w-3" /> Current
              </span>
            )}
            {restoredFromVersion != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600">
                <RotateCcw className="h-3 w-3" /> Restored from v{restoredFromVersion}
              </span>
            )}
          </div>

          <p className={`${BRANDING_LABEL} mt-1 flex flex-wrap items-center gap-1.5`}>
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-background-secondary text-[9px] font-semibold text-text-secondary">
              {initialsOf(item?.publishedByName, item?.publishedByEmail)}
            </span>
            <span>{item?.publishedByName || item?.publishedByEmail || "Unknown"}</span>
            <span aria-hidden="true">·</span>
            <span title={item?.publishedAt ? new Date(item.publishedAt).toLocaleString() : undefined}>
              {formatPublishedAt(item?.publishedAt)}
            </span>
          </p>
        </div>

        <Button
          disabled={disabled || isCurrent}
          onClick={() => onRestore(item)}
          className="inline-flex h-7 shrink-0 items-center justify-center rounded-md border border-border bg-background px-2.5 py-0 text-xs font-medium text-text-primary hover:bg-background-secondary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isCurrent ? "Live" : "Restore"}
        </Button>
      </div>
    </div>
  );
};

const BrandingHistoryPanel = ({
  history,
  currentVersion,
  disabled,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onRestore,
}) => {
  const hasHistory = Array.isArray(history) && history.length > 0;

  // A row's "restored from vN" badge only resolves when that original
  // version is on the same page — it's built from what's loaded here, not a
  // server lookup. Rare in practice (rollbacks are infrequent, and the row
  // being rolled back from is usually recent), so a badge that only shows up
  // when both versions land on the same page is an acceptable trade for not
  // adding a dedicated history-by-id endpoint just for this label.
  const versionById = hasHistory
    ? history.reduce((acc, item) => {
        if (item?.id) acc[item.id] = item.version;
        return acc;
      }, {})
    : {};

  return (
    <div className={`${BRANDING_SHELL} ${BRANDING_CARD_PADDING}`} style={BRANDING_SHELL_STYLE}>
      <BrandingSectionHeader icon={History} title="History" subtitle="Restore a previously published branding version." />

      {hasHistory ? (
        <div className={loading ? "pointer-events-none opacity-50 transition-opacity" : "transition-opacity"}>
          <div className="flex flex-col mb-2">
            {history.map((item, index) => (
              <HistoryRow
                key={item?.id}
                item={item}
                isLast={index === history.length - 1}
                isCurrent={item?.version === currentVersion}
                restoredFromVersion={
                  item?.rolledBackFromHistoryId
                    ? versionById[item.rolledBackFromHistoryId] ?? null
                    : null
                }
                disabled={disabled}
                onRestore={onRestore}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={`${BRANDING_TILE_RADIUS} border border-dashed border-border bg-background-secondary/20 px-3 py-3 text-xs text-text-secondary`}>
          No history available yet. Publish your branding to start building a version history.
        </div>
      )}

      {total > pageSize && (
        <ListPagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          label="versions"
        />
      )}
    </div>
  );
};

export default BrandingHistoryPanel;